// @ts-check

import { mutationOptions, queryOptions } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  createDocument,
  getDocument,
  getDocumentVersion,
  updateDocument,
  deleteDocument,
  acceptDocumentDiff,
  rejectDocumentDiff,
  acceptAllDocumentDiffs,
  rejectAllDocumentDiffs,
  undoDocumentVersion,
  redoDocumentVersion,
  exportDocument,
} from "../api"
import { ChatSessionQueryKeys } from "../sessions/query"

const DocumentQueryKeys = {
  /** @param {string} documentId */
  DOCUMENT: (documentId) => ["documents", documentId],

  /** @param {string} documentId @param {string} versionId */
  DOCUMENT_VERSION: (documentId, versionId) => ["documents", documentId, "versions", versionId],
}

const DocumentQueryOptions = {
  /**
   * @param {{ documentId: string | null | undefined, userToken: string | null | undefined }} params
   */
  DOCUMENT: ({ documentId, userToken }) => queryOptions({
    queryKey: DocumentQueryKeys.DOCUMENT(documentId ?? ""),
    queryFn: ({ signal }) => getDocument(documentId, userToken, { signal }),
    staleTime: 30_000,
    enabled: documentId != null && userToken != null,
  }),

  /**
   * @param {{ documentId: string | null | undefined, versionId: string | null | undefined, userToken: string | null | undefined }} params
   */
  DOCUMENT_VERSION: ({ documentId, versionId, userToken }) => queryOptions({
    queryKey: DocumentQueryKeys.DOCUMENT_VERSION(documentId ?? "", versionId ?? ""),
    queryFn: ({ signal }) => getDocumentVersion(documentId, versionId, userToken, { signal }),
    staleTime: Infinity,
    enabled: documentId != null && versionId != null && userToken != null,
  }),
}

/**
 * Mutation responses only ever include the bare Document (with a
 * `currentVersionId` pointer, not an embedded `currentVersion`), unlike the
 * GET endpoint which computes and embeds `currentVersion`/`canUndo`/`canRedo`.
 * Blindly overwriting the cache with the bare document would make the editor
 * think content just became empty. Merge instead, reconstructing
 * `currentVersion` from whatever `version`/`versions` the mutation returned.
 * @param {import("@tanstack/react-query").QueryClient} client
 * @param {string} documentId
 * @param {{ document?: object, version?: object, versions?: object[] }} data
 */
function mergeDocumentCache(client, documentId, data) {
  const doc = data.document ?? data
  const newVersion = data.version ?? (Array.isArray(data.versions) ? data.versions[data.versions.length - 1] : null)

  client.setQueryData(DocumentQueryKeys.DOCUMENT(documentId), (old) => ({
    ...old,
    ...doc,
    currentVersion: newVersion ? { ...(old?.currentVersion), ...newVersion } : old?.currentVersion,
    canUndo: newVersion ? true : old?.canUndo,
  }))
}

const DocumentMutationOptions = {
  /**
   * @param {{ userToken: string }} params
   */
  CREATE_DOCUMENT: ({ userToken }) => mutationOptions({
    mutationFn: (/** @type {{ title: string, content: string, sessionId: string }} */ data) =>
      createDocument(data, userToken),
    // The chat-session cache's `canvases` list is only populated by the
    // session GET endpoint; without patching it here, activeChatSession.canvases
    // stays stale right after creation, and the auto-open effect in
    // CanvasPanel (keyed off canvases.length) can race with the manual
    // OPEN_CANVAS_PANEL_EVENT and close the panel it just opened.
    async onSuccess(data, variables, _mutateContext, mutationFnContext) {
      const { document, version } = data
      mutationFnContext.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(variables.sessionId),
        (old) => old == null ? old : {
          ...old,
          canvases: [...(old.canvases ?? []), { documentId: document._id, versionId: version._id }],
        }
      )
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string }} params
   */
  UPDATE_DOCUMENT: ({ documentId, userToken }) => mutationOptions({
    mutationFn: (/** @type {{ title?: string, content?: string, sessionId?: string }} */ data) =>
      updateDocument(documentId, data, userToken),
    async onSuccess(data, _variables, _mutateContext, mutationFnContext) {
      mergeDocumentCache(mutationFnContext.client, documentId, data)
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string, sessionId?: string }} params
   */
  ACCEPT_DIFF: ({ documentId, userToken, sessionId }) => mutationOptions({
    mutationFn: (/** @type {{ diffId: string }} */ { diffId }) =>
      acceptDocumentDiff(documentId, diffId, { sessionId }, userToken),
    async onSuccess(data, _variables, _mutateContext, mutationFnContext) {
      mergeDocumentCache(mutationFnContext.client, documentId, data)
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string }} params
   */
  REJECT_DIFF: ({ documentId, userToken }) => mutationOptions({
    mutationFn: (/** @type {{ diffId: string }} */ { diffId }) =>
      rejectDocumentDiff(documentId, diffId, userToken),
    async onSuccess(data, _variables, _mutateContext, mutationFnContext) {
      mergeDocumentCache(mutationFnContext.client, documentId, data)
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string, sessionId?: string }} params
   */
  ACCEPT_ALL_DIFFS: ({ documentId, userToken, sessionId }) => mutationOptions({
    mutationFn: () => acceptAllDocumentDiffs(documentId, { sessionId }, userToken),
    async onSuccess(data, _variables, _mutateContext, mutationFnContext) {
      mergeDocumentCache(mutationFnContext.client, documentId, data)
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string }} params
   */
  REJECT_ALL_DIFFS: ({ documentId, userToken }) => mutationOptions({
    mutationFn: () => rejectAllDocumentDiffs(documentId, userToken),
    async onSuccess(data, _variables, _mutateContext, mutationFnContext) {
      mergeDocumentCache(mutationFnContext.client, documentId, data)
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string }} params
   */
  UNDO: ({ documentId, userToken }) => mutationOptions({
    mutationFn: () => undoDocumentVersion(documentId, userToken),
    async onSuccess(data, _variables, _mutateContext, mutationFnContext) {
      mergeDocumentCache(mutationFnContext.client, documentId, data)
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string }} params
   */
  REDO: ({ documentId, userToken }) => mutationOptions({
    mutationFn: () => redoDocumentVersion(documentId, userToken),
    async onSuccess(data, _variables, _mutateContext, mutationFnContext) {
      mergeDocumentCache(mutationFnContext.client, documentId, data)
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string, sessionId?: string }} params
   */
  DELETE_DOCUMENT: ({ documentId, userToken, sessionId }) => mutationOptions({
    mutationFn: () => deleteDocument(documentId, userToken),
    async onSuccess(_data, _variables, _mutateContext, mutationFnContext) {
      mutationFnContext.client.removeQueries({ queryKey: DocumentQueryKeys.DOCUMENT(documentId) })
      if (sessionId == null) return
      mutationFnContext.client.setQueryData(
        ChatSessionQueryKeys.CHAT_SESSION(sessionId),
        (old) => old == null ? old : {
          ...old,
          canvases: (old.canvases ?? []).filter((canvas) => canvas.documentId !== documentId),
        }
      )
    },
  }),

  /**
   * @param {{ documentId: string, userToken: string }} params
   */
  EXPORT: ({ documentId, userToken }) => mutationOptions({
    mutationFn: (/** @type {{ format: "pdf"|"latex", versionId?: string }} */ { format, versionId }) =>
      exportDocument(documentId, format, userToken, { versionId }),
    onError: () => {
      toast.error("We could not export this document. Please try again.")
    },
  }),
}

export {
  DocumentQueryKeys,
  DocumentQueryOptions,
  DocumentMutationOptions,
}
